import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Building2, MapPin, Users, Sparkles, Star, Plus, Check,
  Pencil, X, Eye, CheckCircle2, ImageIcon, Upload, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* -------------------------------------------------------------------------- */
/*  Inline-edit primitives                                                    */
/* -------------------------------------------------------------------------- */
function InlineEditText({ value, onSave, placeholder, className = '', multiline = false, locked, dataTestId }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || '');
  useEffect(() => setV(value || ''), [value]);

  if (locked) {
    return <span className={className}>{value || <span className="text-muted-foreground italic">{placeholder}</span>}</span>;
  }
  if (editing) {
    const Field = multiline ? Textarea : Input;
    return (
      <div className="flex items-start gap-1">
        <Field
          autoFocus
          value={v}
          rows={multiline ? 3 : undefined}
          onChange={(e) => setV(e.target.value)}
          onBlur={() => { setEditing(false); if (v !== (value || '')) onSave(v); }}
          onKeyDown={(e) => {
            if (!multiline && e.key === 'Enter') { e.target.blur(); }
            if (e.key === 'Escape') { setEditing(false); setV(value || ''); }
          }}
          className="text-sm"
          placeholder={placeholder}
          data-testid={dataTestId}
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className={`group/edit text-left inline-flex items-start gap-1.5 hover:bg-muted/40 rounded px-1 -mx-1 transition ${className}`}
      data-testid={dataTestId ? `${dataTestId}-trigger` : undefined}
    >
      <span className="flex-1 min-w-0">{value || <span className="text-muted-foreground italic">{placeholder || 'Click to edit'}</span>}</span>
      <Pencil className="w-3 h-3 opacity-0 group-hover/edit:opacity-60 mt-0.5 flex-shrink-0" />
    </button>
  );
}

function InlineEditNumber({ value, onSave, locked, prefix = '$', dataTestId }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || 0);
  useEffect(() => setV(value || 0), [value]);
  if (locked) return <span>{prefix}{(value || 0).toLocaleString()}</span>;
  if (editing) {
    return (
      <Input
        autoFocus
        type="number"
        value={v}
        onChange={(e) => setV(parseFloat(e.target.value) || 0)}
        onBlur={() => { setEditing(false); if (v !== (value || 0)) onSave(v); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        className="w-28 text-right text-base font-semibold"
        data-testid={dataTestId}
      />
    );
  }
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); setEditing(true); }} className="hover:bg-muted/40 rounded px-1 -mx-1" data-testid={dataTestId ? `${dataTestId}-trigger` : undefined}>
      {prefix}{(value || 0).toLocaleString()}
      <Pencil className="w-3 h-3 inline ml-1 opacity-50" />
    </button>
  );
}

function InlineEditList({ items, onSave, locked, placeholder = 'Add item', dataTestId }) {
  const [list, setList] = useState(items || []);
  const [draft, setDraft] = useState('');
  useEffect(() => setList(items || []), [items]);

  const commit = (next) => { setList(next); onSave(next); };
  if (locked) {
    return (
      <ul className="space-y-2">
        {(list || []).map((it, i) => (
          <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /><span>{it}</span></li>
        ))}
      </ul>
    );
  }
  return (
    <div className="space-y-2">
      {(list || []).map((it, i) => (
        <div key={i} className="flex items-start gap-2 group/li">
          <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-1.5" />
          <Input
            value={it}
            onChange={(e) => { const next = [...list]; next[i] = e.target.value; setList(next); }}
            onBlur={() => { if (list[i] !== items?.[i]) commit(list); }}
            className="flex-1 text-sm"
          />
          <button type="button" onClick={() => commit(list.filter((_, idx) => idx !== i))} className="opacity-0 group-hover/li:opacity-100 text-muted-foreground hover:text-red-500 transition" aria-label="Remove">
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && draft.trim()) {
              commit([...(list || []), draft.trim()]); setDraft('');
            }
          }}
          placeholder={placeholder}
          className="text-sm"
          data-testid={dataTestId}
        />
        <Button size="sm" variant="outline" onClick={() => { if (draft.trim()) { commit([...(list || []), draft.trim()]); setDraft(''); } }}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

/* Image uploader — public partner upload endpoint, scoped by invitation token. */
function ImageUploader({ value, onChange, locked, invitationToken, listingId, dataTestId }) {
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file || !invitationToken) return;
    if (!file.type.startsWith('image/')) { toast.error('Only image files'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/partner-invitations/public/${invitationToken}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      onChange(fullUrl);
      toast.success('Image uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const generateAi = async () => {
    if (!invitationToken || !listingId) return;
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/partner-invitations/public/${invitationToken}/listings/${listingId}/ai-image`,
        {}
      );
      onChange(res.data.image_url);
      toast.success('AI image generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI generation failed');
    }
    setGenerating(false);
  };

  if (locked) {
    return value ? (
      <div className="aspect-video rounded-lg overflow-hidden bg-muted">
        <img src={value} alt="" className="w-full h-full object-cover" />
      </div>
    ) : null;
  }

  return (
    <div className="space-y-2">
      {value ? (
        <div className="relative group/img aspect-video rounded-lg overflow-hidden bg-muted">
          <img src={value} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 transition flex items-center justify-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => inputRef.current?.click()} disabled={uploading || generating} data-testid={dataTestId ? `${dataTestId}-replace` : undefined}>
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Replace
            </Button>
            {listingId && (
              <Button size="sm" variant="secondary" onClick={generateAi} disabled={uploading || generating} data-testid={dataTestId ? `${dataTestId}-regenerate` : undefined}>
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Re-generate
              </Button>
            )}
            <Button size="sm" variant="destructive" onClick={() => onChange('')}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading || generating}
            className="aspect-video rounded-lg border-2 border-dashed border-border hover:border-indigo-500/60 transition flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-foreground"
            data-testid={dataTestId}
          >
            {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
            <span className="text-sm font-medium">{uploading ? 'Uploading…' : 'Upload image'}</span>
            <span className="text-xs">PNG, JPG, WebP up to 10 MB</span>
          </button>
          {listingId && (
            <button
              type="button"
              onClick={generateAi}
              disabled={uploading || generating}
              className="aspect-video rounded-lg border-2 border-dashed border-fuchsia-500/40 hover:border-fuchsia-500/80 bg-gradient-to-br from-fuchsia-500/5 to-indigo-500/5 transition flex flex-col items-center justify-center gap-2 text-fuchsia-300 hover:text-fuchsia-200"
              data-testid={dataTestId ? `${dataTestId}-ai` : undefined}
            >
              {generating ? <Loader2 className="w-6 h-6 animate-spin" /> : <Sparkles className="w-6 h-6" />}
              <span className="text-sm font-medium">{generating ? 'Generating…' : 'Generate with AI'}</span>
              <span className="text-xs">Nano Banana · 16:9 hero</span>
            </button>
          )}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Service / Staff / Other-category card + modal — mirrors live ServiceGrid  */
/* -------------------------------------------------------------------------- */
function ServiceStyleCard({ listing, selected, locked, onToggle, onOpen, onPriceChange }) {
  const dropoff = listing.has_dropoff_option;
  return (
    <Card
      className={`overflow-hidden hover:shadow-lg transition-shadow cursor-pointer relative ${selected ? 'ring-2 ring-emerald-500/60' : ''}`}
      onClick={onOpen}
      data-testid={`partner-service-card-${listing.listing_id}`}
    >
      {!locked && (
        <div className="absolute top-2 left-2 z-10" onClick={(e) => e.stopPropagation()}>
          <label className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-background/90 backdrop-blur text-xs font-medium border border-border cursor-pointer">
            <Checkbox checked={selected} onCheckedChange={onToggle} data-testid={`approve-listing-${listing.listing_id}`} />
            Approve
          </label>
        </div>
      )}

      {listing.image_url ? (
        <div className="h-44 sm:h-48 overflow-hidden relative bg-muted">
          <img src={listing.image_url} alt={listing.name || listing.title} loading="lazy" className="w-full h-full object-cover" />
          <div className="absolute top-2 right-2 flex gap-1.5">
            {dropoff && <Badge className="bg-emerald-500 text-[10px]">Drop-off</Badge>}
          </div>
        </div>
      ) : (
        <div className="h-44 sm:h-48 flex items-center justify-center bg-gradient-to-br from-indigo-500/10 to-fuchsia-500/10">
          <ImageIcon className="w-10 h-10 text-muted-foreground" />
        </div>
      )}

      <CardHeader className="pb-2">
        <CardTitle className="text-base sm:text-lg line-clamp-2">{listing.name || listing.title}</CardTitle>
        <div className="flex gap-1.5 flex-wrap">
          <Badge variant="secondary" className="text-[10px] capitalize">{listing.category}</Badge>
          {(listing.sub_category || listing.subcategory) && (
            <Badge variant="outline" className="text-[10px] capitalize">{listing.sub_category || listing.subcategory}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pb-2">
        <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">{listing.short_description || listing.description}</p>
        <div className="mt-3 flex items-center justify-between gap-2">
          <div onClick={(e) => e.stopPropagation()}>
            <span className="text-xl sm:text-2xl font-bold text-indigo-500">
              <InlineEditNumber value={listing.base_price} onSave={onPriceChange} locked={locked} dataTestId={`card-price-${listing.listing_id}`} />
            </span>
            <p className="text-[10px] sm:text-xs text-muted-foreground">{listing.pricing_unit_label || 'per event'}</p>
          </div>
          {(listing.option_groups || []).length > 0 && <Badge variant="outline" className="text-xs">Customizable</Badge>}
        </div>
      </CardContent>
      <CardFooter className="pt-2">
        <Button className="w-full" variant="outline" onClick={(e) => { e.stopPropagation(); onOpen(); }} data-testid={`open-listing-${listing.listing_id}`}>
          <Eye className="w-4 h-4 mr-2" /> {locked ? 'View Details' : 'View & Edit'}
        </Button>
      </CardFooter>
    </Card>
  );
}

function ServiceStyleModal({ listing, locked, onClose, onPatch, invitationToken }) {
  const [tab, setTab] = useState('overview');
  if (!listing) return null;
  const patch = (k, v) => onPatch({ [k]: v });

  return (
    <Dialog open={!!listing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0" data-testid="partner-service-modal">
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <div className="flex items-center gap-2 flex-wrap">
              <DialogTitle className="text-xl sm:text-2xl">
                <InlineEditText value={listing.name || listing.title} onSave={(v) => patch('name', v)} placeholder="Listing name" locked={locked} dataTestId="modal-name" />
              </DialogTitle>
            </div>
            <div className="flex gap-1.5 flex-wrap mt-2 items-center">
              <Badge variant="secondary" className="text-xs capitalize">{listing.category}</Badge>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs">
                <InlineEditText value={listing.sub_category || listing.subcategory} onSave={(v) => patch('sub_category', v)} placeholder="sub-category (e.g. wedding)" locked={locked} dataTestId="modal-subcategory" />
              </span>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              <InlineEditText value={listing.short_description} onSave={(v) => patch('short_description', v)} placeholder="One-line tagline" locked={locked} dataTestId="modal-short-desc" />
            </p>
          </DialogHeader>

          {/* Hero / gallery — uploadable */}
          <div className="space-y-3 mt-4">
            <ImageUploader
              value={listing.image_url}
              onChange={(url) => patch('image_url', url)}
              locked={locked}
              invitationToken={invitationToken}
              listingId={listing.listing_id}
              dataTestId="modal-image-upload"
            />
          </div>

          {/* Tabs */}
          <Tabs value={tab} onValueChange={setTab} className="mt-4">
            <TabsList className="w-full justify-start flex-wrap h-auto gap-1">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="included">What's Included</TabsTrigger>
              <TabsTrigger value="requirements">Host Requirements</TabsTrigger>
              {(listing.package_tiers || []).length > 0 && <TabsTrigger value="tiers">Packages</TabsTrigger>}
            </TabsList>

            <TabsContent value="overview" className="space-y-4 mt-4">
              <div>
                <h4 className="font-semibold mb-2">What to Expect</h4>
                <div className="text-muted-foreground" data-testid="modal-what-to-expect-block">
                  <InlineEditText
                    value={listing.what_to_expect || listing.description}
                    onSave={(v) => patch('what_to_expect', v)}
                    placeholder="Describe the experience"
                    multiline
                    locked={locked}
                    className="block"
                    dataTestId="modal-what-to-expect"
                  />
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Description</h4>
                <div className="text-muted-foreground">
                  <InlineEditText
                    value={listing.description}
                    onSave={(v) => patch('description', v)}
                    placeholder="Detailed listing description"
                    multiline
                    locked={locked}
                    className="block"
                    dataTestId="modal-description"
                  />
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Base Price</p>
                    <p className="text-2xl font-bold text-indigo-500">
                      <InlineEditNumber value={listing.base_price} onSave={(v) => patch('base_price', v)} locked={locked} dataTestId="modal-base-price" />
                    </p>
                    <p className="text-xs text-muted-foreground">{listing.pricing_unit_label || 'per event'}</p>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="included" className="mt-4">
              <InlineEditList
                items={listing.whats_included || listing.included_items || []}
                onSave={(v) => patch('whats_included', v)}
                locked={locked}
                placeholder="Add an included item"
                dataTestId="modal-add-included"
              />
            </TabsContent>

            <TabsContent value="requirements" className="mt-4">
              <InlineEditList
                items={listing.host_requirements || []}
                onSave={(v) => patch('host_requirements', v)}
                locked={locked}
                placeholder="Add a host requirement (e.g. power, parking)"
                dataTestId="modal-add-requirement"
              />
            </TabsContent>

            {(listing.package_tiers || []).length > 0 && (
              <TabsContent value="tiers" className="mt-4 space-y-3">
                {listing.package_tiers.map((t, i) => (
                  <div key={i} className="p-4 rounded-lg border border-border">
                    <div className="flex justify-between items-center font-semibold">
                      <InlineEditText value={t.name} onSave={(v) => {
                        const next = [...listing.package_tiers]; next[i] = { ...next[i], name: v }; patch('package_tiers', next);
                      }} locked={locked} placeholder="Tier name" />
                      <span className="text-fuchsia-500">
                        <InlineEditNumber value={t.price} onSave={(v) => {
                          const next = [...listing.package_tiers]; next[i] = { ...next[i], price: v }; patch('package_tiers', next);
                        }} locked={locked} />
                      </span>
                    </div>
                    {Array.isArray(t.features) && t.features.length > 0 && (
                      <ul className="mt-2 text-sm text-muted-foreground list-disc list-inside space-y-1">
                        {t.features.map((f, j) => <li key={j}>{f}</li>)}
                      </ul>
                    )}
                  </div>
                ))}
              </TabsContent>
            )}
          </Tabs>
        </div>

        <div className="border-t bg-background px-4 sm:px-6 py-3 flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">{listing.pricing_unit_label || 'per event'}</p>
            <p className="text-2xl font-black text-indigo-500">${(listing.base_price || 0).toLocaleString()}</p>
          </div>
          <Button onClick={onClose} variant="outline" data-testid="modal-close">Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/*  Venue card + modal — mirrors live VenueCard                               */
/* -------------------------------------------------------------------------- */
function VenueStyleCard({ listing, selected, locked, onToggle, onOpen, onPriceChange }) {
  return (
    <Card
      className={`bg-muted/60 border-border hover:border-indigo-500/30 transition-all group overflow-hidden cursor-pointer relative ${selected ? 'ring-2 ring-emerald-500/60' : ''}`}
      onClick={onOpen}
      data-testid={`partner-venue-card-${listing.listing_id}`}
    >
      {!locked && (
        <div className="absolute top-2 left-2 z-10" onClick={(e) => e.stopPropagation()}>
          <label className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-background/90 backdrop-blur text-xs font-medium border border-border cursor-pointer">
            <Checkbox checked={selected} onCheckedChange={onToggle} data-testid={`approve-listing-${listing.listing_id}`} />
            Approve
          </label>
        </div>
      )}
      <div className="aspect-[16/10] bg-muted overflow-hidden">
        {listing.image_url ? (
          <img src={listing.image_url} alt={listing.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Building2 className="h-10 w-10 text-muted-foreground" />
          </div>
        )}
      </div>
      <CardContent className="p-4">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Badge className="bg-indigo-500/20 text-indigo-400 text-[8px]">VENUE</Badge>
        </div>
        <h3 className="text-sm font-bold text-foreground group-hover:text-indigo-400 transition-colors line-clamp-1">{listing.name || listing.title}</h3>
        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
          <MapPin className="h-3 w-3" />{listing.city || 'Denver'}
        </p>
        {(listing.max_guests || listing.min_guests) && (
          <div className="flex items-center gap-3 mt-2.5 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-0.5"><Users className="h-3 w-3" /> Up to {listing.max_guests || listing.min_guests}</span>
          </div>
        )}
        <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-border">
          <div onClick={(e) => e.stopPropagation()}>
            <p className="text-sm font-bold text-foreground">From <InlineEditNumber value={listing.base_price} onSave={onPriceChange} locked={locked} dataTestId={`card-price-${listing.listing_id}`} /></p>
          </div>
          <span className="text-[10px] text-indigo-400 font-medium group-hover:underline">View Details</span>
        </div>
      </CardContent>
    </Card>
  );
}

function VenueStyleModal({ listing, locked, onClose, onPatch, invitationToken }) {
  if (!listing) return null;
  const patch = (k, v) => onPatch({ [k]: v });

  return (
    <Dialog open={!!listing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] flex flex-col p-0" data-testid="partner-venue-modal">
        <DialogHeader className="sr-only">
          <DialogTitle>{listing.name || listing.title || 'Venue listing preview'}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          {/* Hero — uploadable */}
          <div className="relative">
            {locked ? (
              <div className="aspect-[21/9] bg-muted relative overflow-hidden">
                {listing.image_url ? (
                  <img src={listing.image_url} alt={listing.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-indigo-500/20 to-fuchsia-500/20">
                    <Building2 className="h-20 w-20 text-muted-foreground" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              </div>
            ) : (
              <div className="p-4 bg-muted/30">
                <ImageUploader
                  value={listing.image_url}
                  onChange={(url) => patch('image_url', url)}
                  locked={false}
                  invitationToken={invitationToken}
                  listingId={listing.listing_id}
                  dataTestId="modal-image-upload"
                />
              </div>
            )}
            {locked && (
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <Badge className="bg-indigo-500/80 mb-2">VENUE</Badge>
                <h2 className="text-3xl font-bold">{listing.name || listing.title}</h2>
                <p className="text-sm opacity-90 flex items-center gap-1 mt-1"><MapPin className="w-4 h-4" />{listing.city || 'Denver'}</p>
              </div>
            )}
          </div>

          <div className="p-6 space-y-6">
            {!locked && (
              <div>
                <Badge className="bg-indigo-500/80 mb-2">VENUE</Badge>
                <h2 className="text-3xl font-bold">
                  <InlineEditText value={listing.name || listing.title} onSave={(v) => patch('name', v)} locked={locked} placeholder="Venue name" />
                </h2>
                <p className="text-sm opacity-90 flex items-center gap-1 mt-1"><MapPin className="w-4 h-4" />{listing.city || 'Denver'}</p>
              </div>
            )}

            <div>
              <h4 className="font-semibold mb-2">About this venue</h4>
              <div className="text-muted-foreground">
                <InlineEditText value={listing.description} onSave={(v) => patch('description', v)} multiline locked={locked} placeholder="Tell visitors what makes this venue special" className="block" dataTestId="modal-description" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg border border-border bg-muted/30">
                <p className="text-xs text-muted-foreground mb-1">Capacity</p>
                <p className="text-lg font-bold flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Up to <InlineEditNumber prefix="" value={listing.max_guests} onSave={(v) => patch('max_guests', v)} locked={locked} /> guests
                </p>
              </div>
              <div className="p-4 rounded-lg border border-border bg-muted/30">
                <p className="text-xs text-muted-foreground mb-1">Starting Price</p>
                <p className="text-lg font-bold text-indigo-500">
                  <InlineEditNumber value={listing.base_price} onSave={(v) => patch('base_price', v)} locked={locked} dataTestId="modal-base-price" />
                </p>
                <p className="text-xs text-muted-foreground">{listing.pricing_unit_label || 'per event'}</p>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">What's included</h4>
              <InlineEditList items={listing.whats_included || listing.included_items || []} onSave={(v) => patch('whats_included', v)} locked={locked} placeholder="Add an included amenity" dataTestId="modal-add-included" />
            </div>
          </div>
        </div>

        <div className="border-t bg-background px-6 py-3 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">{locked ? 'Listing locked' : 'Inline-edit any field above'}</p>
          <Button onClick={onClose} variant="outline" data-testid="modal-close">Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/*  Public default export                                                     */
/* -------------------------------------------------------------------------- */
export default function PartnerListingPreview({ listing, partnerCategory, selected, locked, onToggle, onPatch, invitationToken }) {
  const [open, setOpen] = useState(false);
  const isVenue = (partnerCategory || '').toLowerCase() === 'venue';

  const Card_ = isVenue ? VenueStyleCard : ServiceStyleCard;
  const Modal_ = isVenue ? VenueStyleModal : ServiceStyleModal;

  return (
    <>
      <Card_
        listing={listing}
        selected={selected}
        locked={locked}
        onToggle={onToggle}
        onOpen={() => setOpen(true)}
        onPriceChange={(v) => onPatch({ base_price: v })}
      />
      {open && <Modal_ listing={listing} locked={locked} onClose={() => setOpen(false)} onPatch={onPatch} invitationToken={invitationToken} />}
    </>
  );
}
